/**
 * Chess Arena Room – same logic as chess-arenas Arena page.
 * Uses shared useChessGameLogic (configurable timer, same rules/moves/resign/draw).
 * Connects to nest-brc ChessArenasModule (API + WebSocket).
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../store'
import { setSingleRoom } from '../store/roomsSlice'
import roomsService from '../services/roomsService'
import chessArenasService from '../services/chessArenasService'
import publicArenaService from '../services/publicArenaService'
import { webSocketService } from '../services/websocket'
import { useChessGameLogic } from '../hooks/useChessGameLogic'
import ChessBoard from '../components/ChessBoard'
import { ArenaPlayerCard, ArenaGameCard, ArenaWaitlist, CapturedPieces } from '../components/arena'
import InvitePlayerModal from '../components/InvitePlayerModal'
import EditArenaSettingsModal from '../components/EditArenaSettingsModal'
import { normalizeArenaHex } from '../components/ArenaColorPickerField'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'
import { useTranslation } from '../hooks/use-i18n'
import { ArrowLeft, Clock, Eye, Share2, Trophy, History, X, Users, UserPlus, Settings } from 'lucide-react'

interface SeatState {
  isOccupied: boolean
  oderId: string | null
  playerName: string | null
  playerRating: number
}

interface WaitlistPlayer {
  id: string
  name: string
  rating: number
}

const POLL_PUBLIC_MS = 2000

/** Safe default before ResizeObserver runs (avoids 400px board in a ~300px column on first paint). */
function getInitialArenaBoardWidth(): number {
  if (typeof window === 'undefined') return 300
  const w = window.innerWidth
  // Container padding + small safety margin (~48–56px total horizontal)
  return Math.max(260, Math.min(480, Math.floor(w - 56)))
}

interface ChessArenaRoomProps {
  /** When true, page is for unauthenticated public link: same layout, read-only, no play. */
  publicView?: boolean
}

const ChessArenaRoom: React.FC<ChessArenaRoomProps> = ({ publicView: publicViewProp }) => {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { user } = useSelector((state: RootState) => state.auth)
  const singleRoom = useSelector((state: RootState) => state.rooms.singleRoom)
  const publicView = !!publicViewProp

  const [room, setRoom] = useState<{ name: string; arenaId?: string | null; defaultTimeControl?: string } | null>(null)
  const [arena, setArena] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [whiteSeat, setWhiteSeat] = useState<SeatState>({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
  const [blackSeat, setBlackSeat] = useState<SeatState>({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
  const [waitlist, setWaitlist] = useState<WaitlistPlayer[]>([])
  const [isUserInWaitlist, setIsUserInWaitlist] = useState(false)
  const [isTakingSeat, setIsTakingSeat] = useState(false)

  const [drawProposalPending, setDrawProposalPending] = useState(false)
  const [drawProposalFromOpponent, setDrawProposalFromOpponent] = useState(false)
  const [drawProposerName, setDrawProposerName] = useState('')
  const [resignConfirmOpen, setResignConfirmOpen] = useState(false)
  const [seatOffer, setSeatOffer] = useState<{ color: 'white' | 'black'; user: { id: string; name: string; rating: number }; expiresAt: number } | null>(null)

  const [leaderboard, setLeaderboard] = useState<Array<{ id?: string; totalPoints?: number; wins?: number; draws?: number; losses?: number; player?: { name?: string; email?: string } }>>([])
  const [recentGames, setRecentGames] = useState<Array<{ id?: string; result?: string; moves?: string[]; createdAt?: string; whitePlayer?: { name?: string }; blackPlayer?: { name?: string } }>>([])
  const [rankingsModalOpen, setRankingsModalOpen] = useState(false)
  const [leftTab, setLeftTab] = useState<'rankings' | 'recent'>('rankings')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [editArenaSettingsOpen, setEditArenaSettingsOpen] = useState(false)

  const boardContainerRef = useRef<HTMLDivElement>(null)
  const [arenaBoardSize, setArenaBoardSize] = useState(getInitialArenaBoardWidth)

  // Scroll to top when entering the arena so the full top view (header, back, invite) is visible
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [roomId])

  const arenaId = room?.arenaId ?? null

  // Measure board column only after main content mounts (ref is absent during loading spinner).
  const boardLayoutReady =
    !isLoading &&
    !error &&
    (publicView ? !!room : !!arenaId)

  useLayoutEffect(() => {
    if (!boardLayoutReady) return
    const el = boardContainerRef.current
    if (!el) return
    const update = () => {
      const w = el.getBoundingClientRect().width
      if (w > 0) setArenaBoardSize(Math.max(260, Math.floor(w)))
    }
    update()
    const ro = new ResizeObserver(() => update())
    ro.observe(el)
    window.addEventListener('orientationchange', update)
    return () => {
      ro.disconnect()
      window.removeEventListener('orientationchange', update)
    }
  }, [boardLayoutReady, roomId])
  const timeControl = room?.defaultTimeControl ?? '10+0'

  const gameLogic = useChessGameLogic({
    timeControl,
    callbacks: {
      onMove: (move, fen, whiteTimeMs, blackTimeMs) => {
        if (arenaId) webSocketService.emitArenaMove(arenaId, move, fen, whiteTimeMs, blackTimeMs)
      },
      onGameEnd: (winner, reason) => {
        if (arenaId) webSocketService.emitArenaGameEnd(arenaId, winner, reason)
      },
      onTimeExpired: (winner) => {
        if (arenaId) {
          webSocketService.emitArenaGameEnd(arenaId, winner, 'Time expired')
          toast.error(winner === 'white' ? 'Time expired! White wins on time!' : 'Time expired! Black wins on time!')
        }
      },
    },
  })
  const gameLogicRef = useRef(gameLogic)
  gameLogicRef.current = gameLogic

  const whiteSeatRef = useRef(whiteSeat)
  const blackSeatRef = useRef(blackSeat)
  const seatOfferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  whiteSeatRef.current = whiteSeat
  blackSeatRef.current = blackSeat

  useEffect(() => {
    if (seatOfferTimeoutRef.current) clearTimeout(seatOfferTimeoutRef.current)
  }, [])

  // Public view: load via public API and poll (include live board/seats so spectators see real-time state)
  useEffect(() => {
    if (!publicView || !roomId) return
    let cancelled = false
    let first = true
    const load = async () => {
      if (first && !cancelled) setIsLoading(true)
      first = false
      setError(null)
      try {
        const payload = await publicArenaService.getPublicArenaByRoomId(roomId)
        if (cancelled) return
        setRoom({ name: payload.roomName, arenaId: payload.arena?.id ?? null, defaultTimeControl: payload.arena?.defaultTimeControl ?? '10+0' })
        setArena(payload.arena)
        setLeaderboard(Array.isArray(payload.leaderboard) ? payload.leaderboard : [])
        setRecentGames(Array.isArray(payload.games) ? payload.games : [])
        const ls = payload.liveState ?? null
        if (ls) {
          setWhiteSeat(ls.whiteSeat ? { isOccupied: true, oderId: ls.whiteSeat.oderId, playerName: ls.whiteSeat.userName, playerRating: ls.whiteSeat.userRating } : { isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
          setBlackSeat(ls.blackSeat ? { isOccupied: true, oderId: ls.blackSeat.oderId, playerName: ls.blackSeat.userName, playerRating: ls.blackSeat.userRating } : { isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
          setWaitlist(ls.waitlist.map((w) => ({ id: w.id, name: w.name, rating: w.rating })))
          const gl = gameLogicRef.current
          if (gl) {
            gl.setBoardState({ fen: ls.fen, moves: ls.moves, currentTurn: ls.currentTurn, whiteTimeRemaining: ls.whiteTimeRemaining, blackTimeRemaining: ls.blackTimeRemaining, timerStarted: ls.timerStarted })
            gl.setGameStatus(ls.gameStatus)
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          const msg = e?.response?.data?.message ?? e?.message ?? 'Arena not found for this room.'
          setError(Array.isArray(msg) ? msg[0] : msg)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    const interval = setInterval(load, POLL_PUBLIC_MS)
    return () => { cancelled = true; clearInterval(interval) }
  }, [publicView, roomId])

  // Private view: load room then arena
  useEffect(() => {
    if (publicView || !roomId) return
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const roomData = await roomsService.getRoom(roomId)
        if (cancelled) return
        setRoom({
          name: roomData.name,
          arenaId: roomData.arenaId ?? null,
          defaultTimeControl: (roomData as { defaultTimeControl?: string }).defaultTimeControl ?? '10+0',
        })
        dispatch(setSingleRoom(roomData))
        const aid = roomData.arenaId
        if (!aid) {
          setError('This room is not an arena. Go back to the chess room.')
          setIsLoading(false)
          return
        }
        const arenaData = await chessArenasService.getArena(aid)
        if (cancelled) return
        setArena(arenaData)
        await chessArenasService.joinArena(aid).catch(() => {})
        if (cancelled) return
        // Do NOT call connectForArena here: listeners must be registered first (in the effect below) so we don't miss arenaGameState on join
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load arena')
          toast.error('Failed to load arena')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [publicView, roomId, dispatch])

  const fetchLeaderboardAndGames = useCallback(() => {
    if (!arenaId || publicView) return
    chessArenasService.getArenaLeaderboard(arenaId).then((data) => setLeaderboard(Array.isArray(data) ? data : [])).catch(() => {})
    chessArenasService.getArenaGames(arenaId).then((data) => setRecentGames(Array.isArray(data) ? data : [])).catch(() => {})
  }, [arenaId, publicView])

  useEffect(() => {
    if (!arenaId || publicView) return
    fetchLeaderboardAndGames()
  }, [arenaId, publicView, fetchLeaderboardAndGames])

  // Arena WebSocket listeners: connect and subscribe for both authenticated and public viewers so seat changes update in real time for everyone
  useEffect(() => {
    if (!arenaId) return

    const onSeatUpdate = (data: { arenaId: string; color: 'white' | 'black'; seat: any }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      const userId = user?._id
      const isCurrentUser = data.seat?.oderId && String(data.seat.oderId) === String(userId)
      if (data.color === 'white') {
        if (data.seat) {
          setWhiteSeat({ isOccupied: true, oderId: data.seat.oderId, playerName: data.seat.userName, playerRating: data.seat.userRating || 1500 })
          if (isCurrentUser) toast.success('You took the White seat!')
          else toast(`${data.seat.userName} took the White seat`)
        } else {
          setWhiteSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
          toast('White seat is now available')
        }
        if (!data.seat && !blackSeatRef.current.isOccupied) {
          gameLogicRef.current.setGameStatus('waiting')
          gameLogicRef.current.setGameEndInfo(null)
          gameLogicRef.current.resetBoard()
        }
      } else {
        if (data.seat) {
          setBlackSeat({ isOccupied: true, oderId: data.seat.oderId, playerName: data.seat.userName, playerRating: data.seat.userRating || 1500 })
          if (isCurrentUser) toast.success('You took the Black seat!')
          else toast(`${data.seat.userName} took the Black seat`)
        } else {
          setBlackSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
          toast('Black seat is now available')
        }
        if (!data.seat && !whiteSeatRef.current.isOccupied) {
          gameLogicRef.current.setGameStatus('waiting')
          gameLogicRef.current.setGameEndInfo(null)
          gameLogicRef.current.resetBoard()
        }
      }
      setIsTakingSeat(false)
    }

    const onSeatTakeError = (data: { arenaId: string; color: string; error: string }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      if (data.color === 'white') setWhiteSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
      else setBlackSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
      toast.error(data.error)
      setIsTakingSeat(false)
    }

    const onWaitlistUpdate = (data: { arenaId: string; waitlist: any[] }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      setWaitlist(data.waitlist.map((p: any) => ({ id: p.id || p.userId, name: p.name || p.userName, rating: p.rating || p.userRating || 1500 })))
      const uid = user?._id
      setIsUserInWaitlist(data.waitlist.some((p: any) => String(p.id || p.userId) === String(uid)))
    }

    const onSeatOffered = (data: { arenaId: string; color: 'white' | 'black'; user: { id: string; name: string; rating: number }; expiresInMs: number }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      const uid = user?._id
      if (String(data.user.id) !== String(uid)) return
      if (seatOfferTimeoutRef.current) clearTimeout(seatOfferTimeoutRef.current)
      setSeatOffer({ color: data.color, user: data.user, expiresAt: Date.now() + data.expiresInMs })
      seatOfferTimeoutRef.current = setTimeout(() => { setSeatOffer(null); seatOfferTimeoutRef.current = null }, data.expiresInMs)
    }

    const onArenaGameUpdate = (data: { arenaId: string; fen: string; move?: string; currentTurn?: 'white' | 'black'; whiteTimeRemaining?: number; blackTimeRemaining?: number; timerStarted?: boolean }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      const gl = gameLogicRef.current
      const nextMoves = data.move && gl.moves[gl.moves.length - 1] !== data.move ? [...gl.moves, data.move] : gl.moves
      gl.setBoardState({
        fen: data.fen,
        moves: data.move ? nextMoves : undefined,
        currentTurn: data.currentTurn,
        whiteTimeRemaining: data.whiteTimeRemaining,
        blackTimeRemaining: data.blackTimeRemaining,
        timerStarted: data.timerStarted,
      })
    }

    const onArenaGameState = (data: { arenaId: string; fen: string; moves: string[]; currentTurn: 'white' | 'black'; whiteTimeRemaining: number; blackTimeRemaining: number; timerStarted: boolean; gameStatus: string }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      gameLogicRef.current.setBoardState({
        fen: data.fen,
        moves: Array.isArray(data.moves) ? data.moves : [],
        currentTurn: data.currentTurn,
        whiteTimeRemaining: data.whiteTimeRemaining,
        blackTimeRemaining: data.blackTimeRemaining,
        timerStarted: data.timerStarted,
        gameStatus: data.gameStatus === 'playing' ? 'playing' : (data.gameStatus as 'waiting' | 'playing' | 'finished'),
      })
      if (Array.isArray(data.moves) && data.moves.length > 0) {
        toast.success(`Game restored! ${data.moves.length} moves played.`)
      }
    }

    const onArenaGameStarted = () => gameLogicRef.current.setGameStatus('playing')
    const onArenaGameEnded = (data: { arenaId: string; winner: string; reason: string }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      setDrawProposalPending(false)
      setDrawProposalFromOpponent(false)
      setResignConfirmOpen(false)
      gameLogicRef.current.setGameEndInfo({ winner: data.winner as 'white' | 'black' | 'draw' | null, reason: data.reason || 'Game ended' })
      gameLogicRef.current.setGameStatus('finished')
      chessArenasService.getArenaLeaderboard(arenaId).then((d) => setLeaderboard(Array.isArray(d) ? d : [])).catch(() => {})
      chessArenasService.getArenaGames(arenaId).then((d) => setRecentGames(Array.isArray(d) ? d : [])).catch(() => {})
    }
    const onArenaGameReset = () => {
      gameLogicRef.current.resetBoard()
      setDrawProposalPending(false)
      setDrawProposalFromOpponent(false)
      setResignConfirmOpen(false)
      setWhiteSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
      setBlackSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
      toast('Game reset. Ready for new players!')
    }
    const onArenaDrawProposed = (data: { arenaId: string; proposerColor: 'white' | 'black'; proposerName?: string }) => {
      if (String(data?.arenaId ?? '') !== String(arenaId ?? '')) return
      const uid = user?._id
      const isOpponent = (data.proposerColor === 'white' && String(blackSeatRef.current.oderId) === String(uid)) || (data.proposerColor === 'black' && String(whiteSeatRef.current.oderId) === String(uid))
      if (isOpponent) {
        setDrawProposalFromOpponent(true)
        setDrawProposerName(data.proposerName || (data.proposerColor === 'white' ? 'White' : 'Black'))
      }
    }
    const onArenaDrawRejected = () => {
      setDrawProposalPending(false)
      toast('Your opponent declined the draw')
    }

    webSocketService.on('arena:seatUpdate', onSeatUpdate)
    webSocketService.on('arena:seatTakeError', onSeatTakeError)
    webSocketService.on('arena:waitlistUpdate', onWaitlistUpdate)
    webSocketService.on('arena:seatOffered', onSeatOffered)
    webSocketService.on('arena:arenaGameUpdate', onArenaGameUpdate)
    webSocketService.on('arena:arenaGameState', onArenaGameState)
    webSocketService.on('arena:arenaGameStarted', onArenaGameStarted)
    webSocketService.on('arena:arenaGameEnded', onArenaGameEnded)
    webSocketService.on('arena:arenaGameReset', onArenaGameReset)
    webSocketService.on('arena:arenaDrawProposed', onArenaDrawProposed)
    webSocketService.on('arena:arenaDrawRejected', onArenaDrawRejected)

    // Join arena only after listeners are attached so we never miss arenaGameState / seatUpdate on refresh
    webSocketService.connectForArena(arenaId)

    // Re-join arena when tab becomes visible so we always receive seat/game updates (e.g. after tab switch or reconnect)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && arenaId && webSocketService.isConnected()) {
        webSocketService.connectForArena(arenaId)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      webSocketService.leaveArena(arenaId)
      webSocketService.off('arena:seatUpdate')
      webSocketService.off('arena:seatTakeError')
      webSocketService.off('arena:waitlistUpdate')
      webSocketService.off('arena:seatOffered')
      webSocketService.off('arena:arenaGameUpdate')
      webSocketService.off('arena:arenaGameState')
      webSocketService.off('arena:arenaGameStarted')
      webSocketService.off('arena:arenaGameEnded')
      webSocketService.off('arena:arenaGameReset')
      webSocketService.off('arena:arenaDrawProposed')
      webSocketService.off('arena:arenaDrawRejected')
    }
  }, [publicView, arenaId, user?._id])

  const userId = user?._id
  const isUserInWhiteSeat = whiteSeat.isOccupied && String(whiteSeat.oderId) === String(userId)
  const isUserInBlackSeat = blackSeat.isOccupied && String(blackSeat.oderId) === String(userId)
  const isUserPlaying = isUserInWhiteSeat || isUserInBlackSeat
  const playerColor = isUserInBlackSeat ? 'black' : 'white'
  const isPlayerTurn = (gameLogic.currentTurn === 'white' && isUserInWhiteSeat) || (gameLogic.currentTurn === 'black' && isUserInBlackSeat)

  const handleTakeSeat = useCallback((color: 'white' | 'black') => {
    if (!user) { toast.error('Please sign in to play'); return }
    if (isTakingSeat || isUserPlaying) return
    const target = color === 'white' ? whiteSeat : blackSeat
    if (target.isOccupied) { toast.error(`${color === 'white' ? 'White' : 'Black'} seat is already taken!`); return }
    setIsTakingSeat(true)
    const oderId = String(user._id ?? '')
    const displayName = user.name || user.email || 'Player'
    if (isUserInWaitlist && arenaId) {
      webSocketService.emitLeaveWaitlist(arenaId, oderId)
      setWaitlist(prev => prev.filter(p => p.id !== oderId))
      setIsUserInWaitlist(false)
    }
    // Optimistic update so taker sees seat fill immediately; server broadcast will sync others
    if (color === 'white') {
      setWhiteSeat({ isOccupied: true, oderId, playerName: displayName, playerRating: 1500 })
    } else {
      setBlackSeat({ isOccupied: true, oderId, playerName: displayName, playerRating: 1500 })
    }
    if (arenaId && oderId) {
      webSocketService.emitTakeSeat(arenaId, color, { id: oderId, name: displayName, rating: 1500 })
    }
  }, [user, isUserPlaying, isUserInWaitlist, whiteSeat, blackSeat, arenaId, isTakingSeat])

  const handleLeaveSeat = useCallback(() => {
    if (!user) return
    const color = isUserInWhiteSeat ? 'white' : isUserInBlackSeat ? 'black' : null
    if (!color) return
    if (color === 'white') setWhiteSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
    else setBlackSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
    if (arenaId) webSocketService.emitLeaveSeat(arenaId, color)
    toast(`You left the ${color === 'white' ? 'White' : 'Black'} seat`)
    if (gameLogic.gameStatus === 'playing') gameLogic.setGameStatus('finished')
  }, [user, isUserInWhiteSeat, isUserInBlackSeat, arenaId, gameLogic])

  const handleJoinWaitlist = useCallback(() => {
    if (!user) { toast.error('Please sign in to join the waitlist'); return }
    if (isUserPlaying) { toast.error('You are already playing'); return }
    if (isUserInWaitlist) { toast.error('You are already in the waitlist'); return }
    const uid = user._id
    const playerData = { id: uid, name: user.name || user.email || 'Player', rating: 1500 }
    setWaitlist(prev => [...prev, playerData])
    setIsUserInWaitlist(true)
    if (arenaId) webSocketService.emitJoinWaitlist(arenaId, playerData)
    toast.success('Joined the waitlist!')
  }, [user, isUserPlaying, isUserInWaitlist, arenaId])

  const handleLeaveWaitlist = useCallback(() => {
    if (!user) return
    const uid = user._id
    setWaitlist(prev => prev.filter(p => p.id !== uid))
    setIsUserInWaitlist(false)
    if (arenaId && uid) webSocketService.emitLeaveWaitlist(arenaId, uid)
    toast('Left the waitlist')
  }, [user, arenaId])

  const handleMove = useCallback(
    (move: string) => {
      if (gameLogic.gameStatus !== 'playing') return
      if (!isPlayerTurn) {
        toast.error("It's not your turn!")
        return
      }
      gameLogic.handleMove(move)
    },
    [gameLogic, isPlayerTurn],
  )

  const handleResignClick = () => setResignConfirmOpen(true)
  const handleConfirmResign = () => {
    if (!arenaId || gameLogic.gameStatus !== 'playing') return
    setResignConfirmOpen(false)
    const winner = playerColor === 'white' ? 'black' : 'white'
    gameLogic.setGameEndInfo({ winner, reason: 'Resignation' })
    gameLogic.setGameStatus('finished')
    webSocketService.emitArenaGameEnd(arenaId, winner, 'Resignation')
    toast('You have resigned')
  }
  const handleOfferDraw = () => {
    if (!arenaId || gameLogic.gameStatus !== 'playing') return
    setDrawProposalPending(true)
    webSocketService.emitArenaProposeDraw(arenaId, playerColor)
    toast('Draw offer sent. Waiting for opponent...')
  }
  const handleAcceptDraw = () => {
    if (!arenaId || gameLogic.gameStatus !== 'playing') return
    setDrawProposalFromOpponent(false)
    webSocketService.emitArenaAcceptDraw(arenaId, playerColor)
  }
  const handleRejectDraw = () => {
    if (!arenaId) return
    setDrawProposalFromOpponent(false)
    webSocketService.emitArenaRejectDraw(arenaId, playerColor)
    toast('Draw declined')
  }
  const handleAcceptSeatOffer = () => {
    if (!arenaId || !seatOffer || !user) return
    webSocketService.emitAcceptSeatOffer(arenaId, seatOffer.color, user._id)
    if (seatOfferTimeoutRef.current) { clearTimeout(seatOfferTimeoutRef.current); seatOfferTimeoutRef.current = null }
    setSeatOffer(null)
    toast.success(`You took the ${seatOffer.color === 'white' ? 'White' : 'Black'} seat!`)
  }
  const handleRejectSeatOffer = () => {
    if (!arenaId || !seatOffer || !user) return
    webSocketService.emitRejectSeatOffer(arenaId, seatOffer.color, user._id)
    if (seatOfferTimeoutRef.current) { clearTimeout(seatOfferTimeoutRef.current); seatOfferTimeoutRef.current = null }
    setSeatOffer(null)
    toast('Seat offer declined')
  }

  const handleNewGameFreeSeats = () => {
    if (!arenaId) return
    gameLogic.resetBoard()
    setDrawProposalPending(false)
    setDrawProposalFromOpponent(false)
    setResignConfirmOpen(false)
    setWhiteSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
    setBlackSeat({ isOccupied: false, oderId: null, playerName: null, playerRating: 1500 })
    webSocketService.emitResetArenaGame(arenaId)
    toast('Seats cleared. Ready for new game!')
  }

  const copyArenaLink = () => {
    const publicUrl = `${window.location.origin}/public-arena/${roomId ?? ''}`
    navigator.clipboard.writeText(publicUrl).then(() => toast.success(t('chessArena.publicLinkCopied'))).catch(() => toast.error('Could not copy'))
  }

  const timerGameStatus = gameLogic.gameStatus === 'playing' ? 'in-progress' : gameLogic.gameStatus === 'finished' ? 'completed' : 'pending'

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading arena...</p>
        </div>
      </div>
    )
  }

  if (error || (!publicView && !arenaId)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <p className="text-destructive mb-4">{error || t('chessArena.arenaNotFound')}</p>
          <Link to={publicView ? '/' : (roomId ? `/chess/${roomId}` : '/dashboard')} className="text-primary hover:underline">
            ← {t('chessArena.back')} to {publicView ? 'home' : (roomId ? t('chessArena.backToChessRoom') : t('chessArena.backToDashboard'))}
          </Link>
        </div>
      </div>
    )
  }

  const rawArenaPrimary = arena?.primaryColor
  const rawArenaSecondary = arena?.secondaryColor
  const hasArenaBrandingBg = !!(rawArenaPrimary || rawArenaSecondary)
  const hasBannerImage = typeof arena?.banner === 'string' && arena.banner.length > 0
  /** Photo/gradient behind content — used for card contrast and header chrome */
  const hasRichArenaBg = hasArenaBrandingBg || hasBannerImage
  const gradientStart = normalizeArenaHex(String(rawArenaPrimary || rawArenaSecondary || '#1e293b'), '#1e293b')
  const gradientEnd = normalizeArenaHex(String(rawArenaSecondary || rawArenaPrimary || '#334155'), '#334155')
  /** Slightly stronger panels on busy backgrounds (gradient and/or background image) */
  const brandedSurface = hasRichArenaBg ? 'shadow-lg shadow-black/25 ring-1 ring-white/10' : ''

  const canManageArena =
    !!userId &&
    !publicView &&
    !!arenaId &&
    !!arena &&
    (String((arena as { createdBy?: string }).createdBy ?? '') === String(userId) ||
      String(singleRoom?.owner?._id ?? '') === String(userId))

  return (
    <div className="relative min-h-screen flex flex-col">
      {/* Default app background when no arena image or color gradient */}
      {!hasBannerImage && !hasArenaBrandingBg && (
        <div className="absolute inset-0 z-0 bg-background" aria-hidden />
      )}
      {/* Arena "background image" (admin upload) — full-page, cover */}
      {hasBannerImage && (
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${JSON.stringify(arena.banner)})` }}
          aria-hidden
        />
      )}
      {/* Brand colors as gradient overlay (tints the banner if both are set) */}
      {hasArenaBrandingBg && (
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background: `linear-gradient(135deg, ${gradientStart}, ${gradientEnd})`,
            opacity: hasBannerImage ? 0.82 : 1,
          }}
          aria-hidden
        />
      )}
      {/* Banner only: darken slightly so cards and header stay readable */}
      {hasBannerImage && !hasArenaBrandingBg && (
        <div className="absolute inset-0 z-[1] bg-black/45" aria-hidden />
      )}

      <header
        className={`relative z-10 border-b px-2 min-[360px]:px-3 sm:px-4 min-h-[3.25rem] flex items-center justify-between gap-1 ${
          hasRichArenaBg
            ? 'border-white/15 bg-black/25 text-white backdrop-blur-md supports-[backdrop-filter]:bg-black/20'
            : 'border-border'
        }`}
      >
        <Link
          to={publicView ? '/' : (roomId ? `/chess/${roomId}` : '/dashboard')}
          className={`flex items-center gap-1 min-[360px]:gap-1.5 h-10 text-xs min-[360px]:text-sm font-medium flex-shrink-0 z-10 max-w-[38%] sm:max-w-none ${
            hasRichArenaBg ? 'text-white/90 hover:text-white' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" /> <span className="truncate">{t('chessArena.back')}</span>
        </Link>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-1 sm:gap-2 pointer-events-none min-w-0 max-w-[46%] sm:max-w-[min(14rem,50%)] md:max-w-sm px-1">
          {arena?.logo ? (
            <img
              src={arena.logo}
              alt=""
              className="h-7 sm:h-8 w-auto max-w-[72px] sm:max-w-[120px] object-contain shrink-0 xl:hidden"
            />
          ) : null}
          <h1
            className={`text-sm sm:text-lg font-semibold truncate min-w-0 ${arena?.logo ? 'hidden sm:block' : ''} ${
              hasRichArenaBg ? 'text-white' : 'text-foreground'
            }`}
          >
            {room?.name ?? arena?.name ?? 'Arena'}
          </h1>
        </div>
        {publicView ? (
          <div className="w-10 min-[360px]:w-16 sm:w-20 shrink-0" aria-hidden />
        ) : (
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 z-10 max-w-[48%] sm:max-w-none justify-end">
            {canManageArena && (
              <button
                type="button"
                onClick={() => setEditArenaSettingsOpen(true)}
                className={`flex items-center justify-center h-9 sm:h-10 w-9 sm:w-10 rounded-lg text-xs sm:text-sm font-medium border ${
                  hasRichArenaBg
                    ? 'border-white/30 bg-white/10 text-white hover:bg-white/20'
                    : 'border-border bg-background hover:bg-muted text-foreground'
                }`}
                title={t('chessArena.arenaSettings')}
                aria-label={t('chessArena.arenaSettings')}
              >
                <Settings className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowInviteModal(true)}
              className={`flex items-center justify-center gap-1 sm:gap-2 h-9 sm:h-10 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium min-w-0 ${arena?.accentColor ? 'hover:opacity-90' : 'bg-primary text-primary-foreground hover:opacity-90'}`}
              style={arena?.accentColor ? { backgroundColor: arena.accentColor, color: '#fff' } : undefined}
              title={t('roomPage.invitePlayer')}
              aria-label={t('roomPage.invitePlayer')}
            >
              <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span className="hidden sm:inline truncate">{t('roomPage.invitePlayer')}</span>
            </button>
          </div>
        )}
      </header>

      <div className="relative z-10 w-full max-w-6xl xl:max-w-7xl mx-auto min-w-0 flex-1 px-2 py-3 xs:px-3 sm:px-4 sm:py-4 flex flex-col xl:flex-row xl:items-start gap-3 sm:gap-4 xl:gap-6">
        {/* Desktop: arena logo (admin-configured) to the left of Rankings */}
        {arena?.logo ? (
          <div className="hidden xl:flex xl:flex-col xl:order-0 xl:w-28 2xl:w-32 xl:shrink-0 xl:self-start">
            <div
              className={`rounded-xl border border-border bg-card/95 p-2.5 ${brandedSurface}`}
              title={room?.name ?? arena?.name ?? ''}
            >
              <img
                src={arena.logo}
                alt={room?.name ?? arena?.name ?? 'Arena'}
                className="w-full h-auto max-h-36 object-contain object-left-top"
              />
            </div>
          </div>
        ) : null}
        {/* Left sidebar: Rankings | Recent (desktop xl) */}
        <aside className="hidden xl:block xl:w-72 xl:order-1 xl:shrink-0">
          <div className={`rounded-xl border border-border bg-card overflow-hidden ${brandedSurface}`}>
            <div className="flex border-b border-border p-1 gap-0">
              <button type="button" onClick={() => setLeftTab('rankings')} className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md ${leftTab === 'rankings' ? 'bg-muted border-b-0' : ''}`}>
                <Trophy className="w-3.5 h-3.5" /> Rankings
              </button>
              <button type="button" onClick={() => setLeftTab('recent')} className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium rounded-md ${leftTab === 'recent' ? 'bg-muted border-b-0' : ''}`}>
                <History className="w-3.5 h-3.5" /> Recent
              </button>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {leftTab === 'rankings' && leaderboard.map((entry, idx) => (
                <div key={entry.id ?? idx} className="mb-2">
                  <ArenaPlayerCard
                    name={entry.player?.name || entry.player?.email || 'Player'}
                    rank={idx + 1}
                    points={Number(entry.totalPoints ?? 0)}
                    wins={entry.wins ?? 0}
                    draws={entry.draws ?? 0}
                    losses={entry.losses ?? 0}
                    isPlaying={String((entry as any).player?.id ?? (entry as any).playerId ?? '') === String(whiteSeat.oderId) || String((entry as any).player?.id ?? (entry as any).playerId ?? '') === String(blackSeat.oderId)}
                  />
                </div>
              ))}
              {leftTab === 'rankings' && leaderboard.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">{t('chessArena.noRankingsYet')}</p>}
              {leftTab === 'recent' && [...recentGames]
                .sort((a, b) => new Date(b?.createdAt ?? 0).getTime() - new Date(a?.createdAt ?? 0).getTime())
                .slice(0, 20)
                .map((g, idx) => (
                <div key={g.id ?? idx} className="mb-2">
                  <ArenaGameCard
                    whitePlayer={g.whitePlayer?.name ?? 'White'}
                    blackPlayer={g.blackPlayer?.name ?? 'Black'}
                    result={(g.result as 'white' | 'black' | 'draw') ?? 'draw'}
                    date={g.createdAt ? dayjs(g.createdAt).format('MMM D, HH:mm') : ''}
                    moves={g.moves?.length}
                    onReplay={!publicView && roomId && g.id ? () => navigate(`/chess/${roomId}/arena/review/${g.id}`) : undefined}
                  />
                </div>
              ))}
              {leftTab === 'recent' && recentGames.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">{t('chessArena.noGamesYet')}</p>}
            </div>
          </div>
        </aside>

        {/* Center: board column - order matches chess-arenas (opponent top, board middle, your seat bottom when playing) */}
        <div className="flex-1 xl:order-2 xl:max-w-[480px] xl:mx-auto w-full min-w-0">
          <div className="flex flex-col items-stretch sm:items-center gap-1 sm:gap-2 w-full max-w-full min-w-0 sm:max-w-[min(100%,28rem)] sm:mx-auto">
            {/* Black seat - above board when viewer is white, below when viewer is black */}
            <div
              className={`w-full min-w-0 p-2 sm:p-3 rounded-xl border-2 transition-all ${playerColor === 'black' ? 'order-3' : 'order-1'} ${blackSeat.isOccupied ? 'bg-gray-900 border-gray-700' : `border-border border-dashed ${hasRichArenaBg ? 'bg-background/75 backdrop-blur-sm' : 'bg-muted/50'}`}`}
            >
            {blackSeat.isOccupied ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-800 border-2 border-gray-600 flex items-center justify-center text-white font-bold text-sm">{blackSeat.playerName?.[0] ?? 'B'}</div>
                  <div>
                    <p className="font-semibold text-sm truncate">{blackSeat.playerName}</p>
                    <p className="text-xs text-muted-foreground">Rating: {blackSeat.playerRating}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!publicView && isUserInBlackSeat && gameLogic.gameStatus === 'waiting' && (
                    <button type="button" onClick={handleLeaveSeat} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted">Leave</button>
                  )}
                  {gameLogic.gameStatus === 'playing' && (
                    <div className={`px-2 py-1 rounded font-mono text-sm font-bold flex items-center gap-1 ${gameLogic.currentTurn === 'black' ? 'bg-yellow-500/20 text-yellow-400 ring-1 ring-yellow-500/50' : 'bg-gray-800 text-gray-400'}`}>
                      <Clock className="w-3 h-3" />
                      {Math.floor(gameLogic.blackTimeRemaining / 60000)}:{String(Math.floor((gameLogic.blackTimeRemaining % 60000) / 1000)).padStart(2, '0')}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-1">
                {publicView ? (
                  <p className="text-sm text-muted-foreground">Black seat – sign in to play</p>
                ) : user ? (
                  <button type="button" onClick={() => handleTakeSeat('black')} disabled={isUserPlaying || isTakingSeat} className="w-full h-9 rounded bg-gray-900 text-white text-sm font-medium disabled:opacity-50">
                    {isTakingSeat ? t('chessArena.takingSeat') : `${t('chessArena.takeBlackSeat')} ♟`}
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground">Black seat – sign in to play</p>
                )}
              </div>
            )}
            </div>

            {/* Board block: top row = opponent's captures, board, bottom row = your captures (bottom player sees his eaten pieces). */}
            <div className="flex flex-col w-full min-w-0 max-w-full order-2">
              <CapturedPieces fen={gameLogic.fen} className="w-full min-w-0" playerColor={playerColor ?? undefined}>
              <div ref={boardContainerRef} className="relative w-full max-w-full min-w-0 bg-[#302e2b] rounded-lg overflow-hidden" style={{ aspectRatio: '1' }}>
                <div className="w-full h-full min-w-0 flex items-center justify-center overflow-hidden">
                  <ChessBoard
                    fen={gameLogic.fen}
                    onMove={publicView ? () => {} : handleMove}
                    isPlayerTurn={!publicView && isPlayerTurn && gameLogic.gameStatus === 'playing'}
                    playerColor={playerColor}
                    isMakingMove={false}
                    gameStatus={timerGameStatus}
                    boardOrientation={playerColor}
                    moves={gameLogic.moves}
                    boardWidth={arenaBoardSize}
                    hideCaptured
                  />
                </div>
                {publicView && gameLogic.gameStatus !== 'playing' && (
              <div className="absolute inset-0 backdrop-blur-sm flex flex-col items-center justify-center rounded-lg z-10 bg-background/90 text-foreground">
                <p className="text-base font-semibold mb-1">{t('chessArena.publicView')}</p>
                <p className="text-sm text-muted-foreground mb-3">{t('chessArena.signInToPlayToJoin')}</p>
                <Link to="/login" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
                  {t('chessArena.signInToPlay')}
                </Link>
              </div>
            )}
                {!publicView && gameLogic.gameStatus === 'waiting' && !whiteSeat.isOccupied && !blackSeat.isOccupied && (
              <div className="absolute inset-0 backdrop-blur-sm flex flex-col items-center justify-center rounded-lg z-10 bg-background/90 text-foreground">
                <p className="text-base font-semibold mb-1">{t('chessArena.waitingForPlayers')}</p>
                <p className="text-sm text-muted-foreground">{t('chessArena.takeSeatToPlay')}</p>
              </div>
            )}
            {!publicView && gameLogic.gameStatus === 'finished' && gameLogic.gameEndInfo && (
              <div className="absolute inset-0 backdrop-blur-md flex flex-col items-center justify-center rounded-lg z-10 bg-background/95 text-foreground p-4">
                <p className="text-lg font-bold mb-1">
                  {gameLogic.gameEndInfo.winner === 'draw' ? 'Draw' : `${gameLogic.gameEndInfo.winner === 'white' ? 'White' : 'Black'} wins`}
                </p>
                <p className="text-sm text-muted-foreground mb-4">{gameLogic.gameEndInfo.reason}</p>
                <button type="button" onClick={handleNewGameFreeSeats} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium mb-2">
                  New game — free both seats
                </button>
                <Link to={roomId ? `/chess/${roomId}` : '/dashboard'} className="text-sm text-primary hover:underline">
                  {t('chessArena.back')} to {roomId ? t('chessArena.backToChessRoom') : t('chessArena.backToDashboard')}
                </Link>
              </div>
            )}
                {/* Spectator pill - matches chess-arenas */}
                {!publicView && !isUserPlaying && gameLogic.gameStatus === 'playing' && (
                  <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/50 backdrop-blur-sm flex items-center gap-1.5 z-10">
                    <Eye className="w-3 h-3 text-white" />
                    <span className="text-white text-xs">Spectating</span>
                  </div>
                )}
              </div>
              </CapturedPieces>
            </div>

            {/* White seat - below board when viewer is white, above when viewer is black */}
            <div
              className={`w-full min-w-0 p-2 sm:p-3 rounded-xl border-2 transition-all ${playerColor === 'black' ? 'order-1' : 'order-3'} ${whiteSeat.isOccupied ? 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600' : `border-border border-dashed ${hasRichArenaBg ? 'bg-background/75 backdrop-blur-sm' : 'bg-muted/50'}`}`}
            >
            {whiteSeat.isOccupied ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-800 dark:text-white font-bold text-sm">{whiteSeat.playerName?.[0] ?? 'W'}</div>
                  <div>
                    <p className="font-semibold text-sm truncate">{whiteSeat.playerName}</p>
                    <p className="text-xs text-muted-foreground">Rating: {whiteSeat.playerRating}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!publicView && isUserInWhiteSeat && gameLogic.gameStatus === 'waiting' && (
                    <button type="button" onClick={handleLeaveSeat} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted">Leave</button>
                  )}
                  {gameLogic.gameStatus === 'playing' && (
                    <div className={`px-2 py-1 rounded font-mono text-sm font-bold flex items-center gap-1 ${gameLogic.currentTurn === 'white' ? 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 ring-1 ring-yellow-500/50' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}>
                      <Clock className="w-3 h-3" />
                      {Math.floor(gameLogic.whiteTimeRemaining / 60000)}:{String(Math.floor((gameLogic.whiteTimeRemaining % 60000) / 1000)).padStart(2, '0')}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-1">
                {publicView ? (
                  <p className="text-sm text-muted-foreground">White seat – sign in to play</p>
                ) : user ? (
                  <button type="button" onClick={() => handleTakeSeat('white')} disabled={isUserPlaying || isTakingSeat} className="w-full h-9 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 text-sm font-medium disabled:opacity-50">
                    {isTakingSeat ? t('chessArena.takingSeat') : `${t('chessArena.takeWhiteSeat')} ♙`}
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground">White seat – sign in to play</p>
                )}
              </div>
            )}
          </div>

            {/* Resign / Offer Draw - order-4 */}
            {!publicView && gameLogic.gameStatus === 'playing' && isUserPlaying && (
              <div className="w-full min-w-0 flex gap-1.5 sm:gap-2 order-4">
                <button type="button" onClick={handleResignClick} className="flex-1 min-w-0 py-2 rounded-lg border border-destructive text-destructive hover:bg-destructive/10 text-[11px] xs:text-xs sm:text-sm font-medium">
                  {t('chessArena.resign')}
                </button>
                {drawProposalPending ? (
                  <button type="button" disabled className="flex-1 min-w-0 py-2 rounded-lg border border-border text-muted-foreground text-[11px] xs:text-xs sm:text-sm">
                    {t('chessArena.waitingForOpponent')}
                  </button>
                ) : (
                  <button type="button" onClick={handleOfferDraw} className="flex-1 min-w-0 py-2 rounded-lg border border-border hover:bg-muted text-[11px] xs:text-xs sm:text-sm font-medium">
                    {t('chessArena.offerDraw')}
                  </button>
                )}
              </div>
            )}

            {/* Move history - order-5 */}
            {gameLogic.moves.length > 0 && (
              <div
                className={`w-full max-w-full sm:max-w-[min(100%,360px)] rounded-lg p-2 sm:p-3 order-5 min-w-0 ${hasRichArenaBg ? 'bg-background/80 backdrop-blur-sm border border-border/80' : 'bg-muted/50'}`}
              >
                <p className="text-xs font-medium text-muted-foreground mb-2">{t('chessArena.moves')}: {gameLogic.moves.length}</p>
                <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                  {gameLogic.moves.map((move, i) => (
                    <span key={i} className="px-1.5 py-0.5 rounded text-xs font-mono bg-background text-foreground">
                      {move}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar: Waitlist, Arena Info, Share */}
        <aside className="w-full min-w-0 xl:w-72 xl:order-3 xl:shrink-0 space-y-3 sm:space-y-4">
          <ArenaWaitlist
            players={publicView ? [] : waitlist}
            isGameOngoing={!publicView && gameLogic.gameStatus === 'playing'}
            isUserInWaitlist={isUserInWaitlist}
            onJoinWaitlist={handleJoinWaitlist}
            onLeaveWaitlist={handleLeaveWaitlist}
            isAuthenticated={!publicView && !!user}
            accentColor={arena?.accentColor ? normalizeArenaHex(String(arena.accentColor), '#3b9b78') : undefined}
            surfaceClassName={brandedSurface}
          />
          <div className={`rounded-xl border border-border p-3 bg-card ${brandedSurface}`}>
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" /> Arena Info
            </h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium capitalize">{publicView ? 'view only' : (gameLogic.gameStatus === 'playing' ? 'playing' : gameLogic.gameStatus === 'finished' ? 'game over' : 'waiting for players')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Waitlist</span>
                <span className="font-medium">{publicView ? '—' : `${waitlist.length} players`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Games</span>
                <span className="font-medium">{recentGames.length}</span>
              </div>
            </div>
          </div>
          <div className={`rounded-xl border border-border p-3 bg-card ${brandedSurface}`}>
            <p className="text-xs mb-2 font-medium text-muted-foreground">{t('chessArena.shareThisArena')}</p>
            <button type="button" onClick={copyArenaLink} className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-border hover:bg-muted text-xs font-medium">
              <Share2 className="w-3 h-3" /> {t('chessArena.copyLink')}
            </button>
          </div>
        </aside>

        {/* Mobile: Rankings & Recent button */}
        <div className="xl:hidden w-full flex justify-center">
          <button
            type="button"
            onClick={() => setRankingsModalOpen(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-card text-sm font-medium ${brandedSurface}`}
          >
            <History className="w-4 h-4" /> {t('chessArena.rankingsAndRecentGames')}
          </button>
        </div>
      </div>

      {/* Mobile modal: Rankings | Recent */}
      {rankingsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setRankingsModalOpen(false)}>
          <div className="bg-card border border-border rounded-xl shadow-xl max-w-md w-full max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b border-border">
              <span className="font-semibold">{t('chessArena.rankingsAndRecent')}</span>
              <button type="button" onClick={() => setRankingsModalOpen(false)} className="p-1 rounded hover:bg-muted"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex border-b border-border">
              <button type="button" onClick={() => setLeftTab('rankings')} className={`flex-1 py-2.5 text-sm font-medium ${leftTab === 'rankings' ? 'bg-muted border-b-2 border-primary' : ''}`}>{t('chessArena.rankings')}</button>
              <button type="button" onClick={() => setLeftTab('recent')} className={`flex-1 py-2.5 text-sm font-medium ${leftTab === 'recent' ? 'bg-muted border-b-2 border-primary' : ''}`}>{t('chessArena.recent')}</button>
            </div>
            <div className="p-2 max-h-[60vh] overflow-y-auto">
              {leftTab === 'rankings' && leaderboard.map((entry, idx) => (
                <div key={entry.id ?? idx} className="mb-2">
                  <ArenaPlayerCard name={entry.player?.name || entry.player?.email || 'Player'} rank={idx + 1} points={Number(entry.totalPoints ?? 0)} wins={entry.wins ?? 0} draws={entry.draws ?? 0} losses={entry.losses ?? 0} />
                </div>
              ))}
              {leftTab === 'rankings' && leaderboard.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">{t('chessArena.noRankingsYet')}</p>}
              {leftTab === 'recent' && [...recentGames]
                .sort((a, b) => new Date(b?.createdAt ?? 0).getTime() - new Date(a?.createdAt ?? 0).getTime())
                .slice(0, 20)
                .map((g, idx) => (
                <div key={g.id ?? idx} className="mb-2">
                  <ArenaGameCard
                    whitePlayer={g.whitePlayer?.name ?? 'White'}
                    blackPlayer={g.blackPlayer?.name ?? 'Black'}
                    result={(g.result as 'white' | 'black' | 'draw') ?? 'draw'}
                    date={g.createdAt ? dayjs(g.createdAt).format('MMM D, HH:mm') : ''}
                    moves={g.moves?.length}
                    onReplay={!publicView && roomId && g.id ? () => { setRankingsModalOpen(false); navigate(`/chess/${roomId}/arena/review/${g.id}`); } : undefined}
                  />
                </div>
              ))}
              {leftTab === 'recent' && recentGames.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">{t('chessArena.noGamesYet')}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Draw proposal from opponent */}
      {drawProposalFromOpponent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-xl p-4 max-w-sm w-full shadow-xl">
            <p className="font-semibold mb-2">{drawProposerName} {t('chessArena.offeredDraw')}</p>
            <div className="flex gap-2">
              <button type="button" onClick={handleAcceptDraw} className="flex-1 py-2 rounded bg-primary text-primary-foreground text-sm font-medium">{t('chessArena.accept')}</button>
              <button type="button" onClick={handleRejectDraw} className="flex-1 py-2 rounded border border-border hover:bg-muted text-sm">{t('chessArena.decline')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Resign confirm */}
      {resignConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-xl p-4 max-w-sm w-full shadow-xl">
            <p className="font-semibold mb-2">{t('chessArena.resignConfirm')}</p>
            <div className="flex gap-2">
              <button type="button" onClick={handleConfirmResign} className="flex-1 py-2 rounded bg-destructive text-destructive-foreground text-sm font-medium">{t('chessArena.resign')}</button>
              <button type="button" onClick={() => setResignConfirmOpen(false)} className="flex-1 py-2 rounded border border-border hover:bg-muted text-sm">{t('chessArena.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Seat offer */}
      {seatOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-xl p-4 max-w-sm w-full shadow-xl">
            <p className="font-semibold mb-2">{t('chessArena.youWereOfferedSeat', { color: seatOffer?.color === 'black' ? t('chessArena.colorBlack') : t('chessArena.colorWhite') })}</p>
            <div className="flex gap-2">
              <button type="button" onClick={handleAcceptSeatOffer} className="flex-1 py-2 rounded bg-primary text-primary-foreground text-sm font-medium">{t('chessArena.accept')}</button>
              <button type="button" onClick={handleRejectSeatOffer} className="flex-1 py-2 rounded border border-border hover:bg-muted text-sm">{t('chessArena.decline')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Invite player to room (same as football / formula 1) */}
      {showInviteModal && roomId && (
        <InvitePlayerModal
          roomId={roomId}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false)
            toast.success(t('chessArena.invitationSent'))
          }}
        />
      )}

      {editArenaSettingsOpen && arenaId && (
        <EditArenaSettingsModal
          isOpen={editArenaSettingsOpen}
          onClose={() => setEditArenaSettingsOpen(false)}
          arenaId={arenaId}
          arena={arena}
          onSaved={(updated) => {
            setArena(updated)
            setRoom((r) =>
              r
                ? {
                    ...r,
                    defaultTimeControl:
                      typeof updated.defaultTimeControl === 'string' && updated.defaultTimeControl
                        ? updated.defaultTimeControl
                        : r.defaultTimeControl,
                  }
                : r
            )
          }}
        />
      )}
    </div>
  )
}

export default ChessArenaRoom
